import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import StorageService from '../services/storage/storageService';

const AssessmentContext = createContext();

export const useAssessment = () => {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessment must be used within AssessmentProvider');
  }
  return context;
};

export const AssessmentProvider = ({ children }) => {
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [assessmentStep, setAssessmentStep] = useState('personal-info'); // personal-info, nutrition, cognitive, physical, complete
  const [personalInfo, setPersonalInfo] = useState(null);
  const [nutritionResponses, setNutritionResponses] = useState([]);
  const [cognitiveResponses, setCognitiveResponses] = useState([]);
  const [physicalTests, setPhysicalTests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Initialize or load existing assessment
  useEffect(() => {
    const loadAssessment = async () => {
      const savedId = sessionStorage.getItem('currentAssessmentId');
      if (savedId) {
        const assessment = await StorageService.getAssessment(savedId);
        if (assessment && assessment.status !== 'completed') {
          setCurrentAssessment(assessment);
          setAssessmentStep(assessment.currentStep || 'personal-info');
          setPersonalInfo(assessment.personalInfo);
          setNutritionResponses(assessment.nutritionResponses || []);
          setCognitiveResponses(assessment.cognitiveResponses || []);
          setPhysicalTests(assessment.physicalTests || []);
        }
      }
    };
    loadAssessment();
  }, []);

  // Start new assessment
  const startAssessment = async (personalData) => {
    const assessmentId = uuidv4();
    const assessment = {
      id: assessmentId,
      personalInfo: personalData,
      nutritionResponses: [],
      cognitiveResponses: [],
      physicalTests: [],
      reports: null,
      status: 'in_progress',
      currentStep: 'nutrition',
      createdAt: new Date().toISOString(),
      completedAt: null
    };

    const result = await StorageService.saveAssessment(assessment);
    
    // Use the ID returned from the database (if API is used)
    const actualId = result?.data?.id || assessmentId;
    assessment.id = actualId;
    
    sessionStorage.setItem('currentAssessmentId', actualId);
    
    setCurrentAssessment(assessment);
    setPersonalInfo(personalData);
    setAssessmentStep('nutrition');
    
    return actualId;
  };

  // Save nutrition response
  const saveNutritionResponse = async (questionId, answer, score) => {
    const response = { questionId, answer, score, timestamp: new Date().toISOString() };
    const updatedResponses = [...nutritionResponses, response];
    setNutritionResponses(updatedResponses);

    if (currentAssessment) {
      await StorageService.updateAssessment(currentAssessment.id, {
        nutritionResponses: updatedResponses
      });
    }
  };

  // Save cognitive response
  const saveCognitiveResponse = async (questionId, answer, score) => {
    const response = { questionId, answer, score, timestamp: new Date().toISOString() };
    const updatedResponses = [...cognitiveResponses, response];
    setCognitiveResponses(updatedResponses);

    if (currentAssessment) {
      await StorageService.updateAssessment(currentAssessment.id, {
        cognitiveResponses: updatedResponses
      });
    }
  };

  // Save physical test result
  const savePhysicalTest = async (testType, results) => {
    // Results already contains testId, testName, metrics, duration, completedAt
    // Just add it directly to the array
    const updatedTests = [...physicalTests, results];
    setPhysicalTests(updatedTests);

    if (currentAssessment) {
      await StorageService.updateAssessment(currentAssessment.id, {
        physicalTests: updatedTests
      });
    }
  };

  // Move to next step
  const nextStep = async () => {
    const steps = ['personal-info', 'nutrition', 'cognitive', 'physical', 'complete'];
    const currentIndex = steps.indexOf(assessmentStep);
    const nextStepValue = steps[currentIndex + 1];
    
    setAssessmentStep(nextStepValue);
    
    if (currentAssessment) {
      await StorageService.updateAssessment(currentAssessment.id, {
        currentStep: nextStepValue
      });
    }
  };

  // Complete assessment
  const completeAssessment = async () => {
    if (currentAssessment) {
      await StorageService.updateAssessment(currentAssessment.id, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        currentStep: 'complete'
      });
      
      setAssessmentStep('complete');
      sessionStorage.removeItem('currentAssessmentId');
    }
  };

  // Reset assessment
  const resetAssessment = () => {
    setCurrentAssessment(null);
    setAssessmentStep('personal-info');
    setPersonalInfo(null);
    setNutritionResponses([]);
    setCognitiveResponses([]);
    setPhysicalTests([]);
    sessionStorage.removeItem('currentAssessmentId');
  };

  const value = {
    currentAssessment,
    assessmentStep,
    personalInfo,
    nutritionResponses,
    cognitiveResponses,
    physicalTests,
    isLoading,
    startAssessment,
    saveNutritionResponse,
    saveCognitiveResponse,
    savePhysicalTest,
    nextStep,
    completeAssessment,
    resetAssessment,
    setIsLoading
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
};
